import {
  ghostBtnClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { ComposeEntryMode, ComposePlanSummary } from "@/lib/manufacturing/composePlan";

/** Entry: resume org Compose drafts or start a new one. */
export function ManufacturingModelsComposeEntry({
  savedPlans,
  busy,
  onResume,
  onDiscard,
  onStart,
  onImportJson,
}: {
  savedPlans: readonly ComposePlanSummary[];
  busy?: boolean;
  onResume: (planId: string) => void;
  onDiscard: (planId: string) => void;
  onStart: (mode: ComposeEntryMode) => void;
  onImportJson: (raw: string) => void;
}) {
  return (
    <div className="space-y-3">
      {savedPlans.length > 0 ? (
        <div className="space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Resume org plan
          </div>
          {savedPlans.map((s) => (
            <div
              key={s.planId}
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-zinc-700"
            >
              <div>
                <div className="font-medium">{s.seriesName || s.seriesCode || s.planId}</div>
                <div className="text-[11px] text-slate-500">
                  {s.status ? `${s.status} · ` : ""}
                  {s.entryMode} · step {s.step} · {s.committedProducts}/{s.productCount} products
                  committed · updated {new Date(s.updatedAt).toLocaleString()}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className={mfgPrimaryBtnClass}
                  disabled={busy}
                  onClick={() => onResume(s.planId)}
                >
                  Continue
                </button>
                <button
                  type="button"
                  className={ghostBtnClass}
                  disabled={busy}
                  onClick={() => onDiscard(s.planId)}
                >
                  Discard
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : null}
      <p className="text-sm text-slate-600 dark:text-slate-300">
        Start a new Compose plan (saved for the org — admin, owner, and manufacturing roles can
        continue it).
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={mfgPrimaryBtnClass}
          disabled={busy}
          onClick={() => onStart("bottom_up")}
        >
          Bottom-up archetype
        </button>
        <button
          type="button"
          className={mfgPrimaryBtnClass}
          disabled={busy}
          onClick={() => onStart("top_down")}
        >
          Top-down SKU list
        </button>
      </div>
      <label className="block text-xs text-slate-500">
        Import plan JSON
        <input
          type="file"
          accept="application/json,.json"
          className="mt-1 block text-xs"
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            void file.text().then((t) => onImportJson(t));
          }}
        />
      </label>
    </div>
  );
}
